import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Search, User, Heart, LogOut, LayoutDashboard, Menu, X, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth, useCart } from "@/lib/store";
import { LogoMark, LogoWordmark } from "@/components/Logo";
import AnnouncementBar from "@/components/AnnouncementBar";
import CountdownBanner from "@/components/CountdownBanner";
import FreeShippingBar from "@/components/FreeShippingBar";
import ExitIntentModal from "@/components/ExitIntentModal";
import CartFooter from "@/components/CartFooter";
import UpsellModal from "@/components/UpsellModal";
import SpinWheel from "@/components/SpinWheel";
import ReferralWidget from "@/components/ReferralWidget";
import LiveOrderToast from "@/components/LiveOrderToast";

export default function Layout({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const { items, count, subtotal, open, setOpen, update, remove } = useCart();
  const [q, setQ] = React.useState("");
  const [menu, setMenu] = React.useState(false);

  const onSearch = (e) => { e.preventDefault(); if (q.trim()) nav(`/shop?search=${encodeURIComponent(q.trim())}`); };

  return (
    <div className="min-h-screen">
      <AnnouncementBar />
      <CountdownBanner />
      <header className="sticky top-0 z-40 glass" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <LogoMark size={32} />
              <LogoWordmark className="text-xl" />
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/shop" className="hover:text-white" data-testid="nav-shop">Shop</Link>
              <Link to="/shop?category=earbuds" className="hover:text-white">Audio</Link>
              <Link to="/shop?category=smartwatches" className="hover:text-white">Wearables</Link>
              <Link to="/shop?category=gaming-gear" className="hover:text-white">Gaming</Link>
              <Link to="/shop?category=smart-home" className="hover:text-white">Smart Home</Link>
            </nav>
            <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md relative" data-testid="search-form">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search earbuds, keyboards, chargers..."
                className="pl-9 bg-secondary/60 border-white/10" data-testid="search-input" />
            </form>
            <div className="flex items-center gap-1">
              {user?.role === "admin" && (
                <Button variant="ghost" size="icon" onClick={() => nav("/admin")} data-testid="nav-admin" className="hidden md:inline-flex">
                  <LayoutDashboard className="h-5 w-5" />
                </Button>
              )}
              {user ? (
                <>
                  <Button variant="ghost" size="icon" onClick={() => nav("/account")} data-testid="nav-account" className="hidden md:inline-flex">
                    <User className="h-5 w-5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={logout} data-testid="nav-logout" className="hidden md:inline-flex">
                    <LogOut className="h-5 w-5" />
                  </Button>
                </>
              ) : (
                <>
                  {/* Mobile: compact "Sign in" pill BEFORE the cart, always visible so shoppers can log in from home */}
                  <button
                    onClick={() => nav("/login", { state: { from: loc.pathname }})}
                    className="md:hidden px-3 h-8 rounded-full bg-primary/15 text-primary text-xs font-semibold border border-primary/30 active:scale-95 transition"
                    data-testid="nav-login-mobile"
                  >
                    Sign in
                  </button>
                  <Button variant="ghost" size="sm" onClick={() => nav("/login", { state: { from: loc.pathname }})} data-testid="nav-login" className="hidden md:inline-flex">Sign in</Button>
                </>
              )}
              <Button variant="ghost" size="icon" onClick={() => nav("/account?tab=wishlist")} data-testid="nav-wishlist" className="hidden md:inline-flex">
                <Heart className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" className="relative" onClick={() => setOpen(true)} data-testid="nav-cart">
                <ShoppingCart className="h-5 w-5" />
                {count > 0 && <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">{count}</span>}
              </Button>
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMenu(!menu)} data-testid="nav-menu">
                {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
          {menu && (
            <div className="md:hidden pb-4 flex flex-col gap-3 text-sm">
              <form onSubmit={onSearch} className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9" />
              </form>
              <div className="grid grid-cols-2 gap-2">
                <Link to="/shop" onClick={() => setMenu(false)} className="rounded-lg bg-secondary/40 px-3 py-2 text-center">All Products</Link>
                <Link to="/shop?category=earbuds" onClick={() => setMenu(false)} className="rounded-lg bg-secondary/40 px-3 py-2 text-center">Audio</Link>
                <Link to="/shop?category=smartwatches" onClick={() => setMenu(false)} className="rounded-lg bg-secondary/40 px-3 py-2 text-center">Wearables</Link>
                <Link to="/shop?category=gaming-gear" onClick={() => setMenu(false)} className="rounded-lg bg-secondary/40 px-3 py-2 text-center">Gaming</Link>
                <Link to="/shop?category=smart-home" onClick={() => setMenu(false)} className="rounded-lg bg-secondary/40 px-3 py-2 text-center">Smart Home</Link>
                <Link to="/account?tab=wishlist" onClick={() => setMenu(false)} className="rounded-lg bg-secondary/40 px-3 py-2 text-center flex items-center justify-center gap-1"><Heart className="h-3.5 w-3.5" /> Wishlist</Link>
              </div>
              {user && (
                <Link
                  to="/account?tab=orders"
                  onClick={() => setMenu(false)}
                  className="rounded-lg bg-primary/15 text-primary border border-primary/30 px-3 py-3 text-center font-semibold flex items-center justify-center gap-2"
                  data-testid="mobile-my-orders"
                >
                  <Package className="h-4 w-4" /> Track my orders
                </Link>
              )}
              <div className="border-t border-white/5 pt-3 flex gap-2">
                {user ? (
                  <>
                    <button onClick={() => { setMenu(false); nav("/account"); }} className="flex-1 rounded-lg bg-secondary/60 px-3 py-2 text-center flex items-center justify-center gap-2"><User className="h-4 w-4" /> Account</button>
                    {user?.role === "admin" && (
                      <button onClick={() => { setMenu(false); nav("/admin"); }} className="flex-1 rounded-lg bg-primary/15 text-primary px-3 py-2 text-center flex items-center justify-center gap-2"><LayoutDashboard className="h-4 w-4" /> Admin</button>
                    )}
                    <button onClick={() => { setMenu(false); logout(); }} className="rounded-lg bg-secondary/40 px-3 py-2 text-muted-foreground"><LogOut className="h-4 w-4" /></button>
                  </>
                ) : (
                  <button onClick={() => { setMenu(false); nav("/login", { state: { from: loc.pathname }}); }} className="flex-1 rounded-lg bg-primary text-primary-foreground font-semibold px-3 py-2 text-center">Sign in</button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <main data-testid="main-content" className="overflow-x-hidden">{children}</main>

      <footer className="border-t border-white/5 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <LogoMark size={24} withGlow={false} />
              <LogoWordmark className="text-sm" />
            </div>
            <p className="text-muted-foreground">Premium electronics, delivered fast. Curated gadgets for every setup.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Shop</p>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/shop?category=earbuds">Earbuds</Link></li>
              <li><Link to="/shop?category=headphones">Headphones</Link></li>
              <li><Link to="/shop?category=gaming-gear">Gaming</Link></li>
              <li><Link to="/shop?category=smart-home">Smart Home</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Support</p>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/account?tab=orders" className="hover:text-primary transition">Order tracking</Link></li>
              <li><Link to="/refund-policy" className="hover:text-primary transition">Shipping & returns</Link></li>
              <li><Link to="/refund-policy" className="hover:text-primary transition">Warranty</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Company</p>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/contact" className="hover:text-primary transition">About</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-primary transition">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-primary transition">Refund Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-6 text-center text-xs text-muted-foreground">© 2026 NovalynxTech. All rights reserved.</div>
      </footer>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="bg-card border-white/10 w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
          <SheetHeader><SheetTitle>Your cart ({count})</SheetTitle><SheetDescription className="text-muted-foreground text-sm" data-testid="cart-drawer-description">Review items before checkout.</SheetDescription></SheetHeader>
          <div className="flex-1 overflow-auto py-4 space-y-4">
            {items.length === 0 && <p className="text-muted-foreground text-sm">Your cart is empty.</p>}
            {items.map((it, i) => (
              <div key={i} className="flex gap-3" data-testid={`cart-item-${i}`}>
                <img src={it.image} alt="" className="h-16 w-16 rounded-lg object-cover bg-muted" />
                <div className="flex-1">
                  <p className="text-sm font-medium line-clamp-1">{it.title}</p>
                  <p className="text-xs text-muted-foreground">${it.unit_price.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <button onClick={() => update(i, it.quantity - 1)} className="h-6 w-6 rounded bg-secondary">−</button>
                    <span className="text-sm">{it.quantity}</span>
                    <button onClick={() => update(i, it.quantity + 1)} className="h-6 w-6 rounded bg-secondary">+</button>
                    <button onClick={() => remove(i)} className="ml-auto text-xs text-muted-foreground hover:text-destructive">Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {items.length > 0 && (
            <CartFooter
              items={items}
              subtotal={subtotal}
              onCheckout={() => { setOpen(false); nav("/checkout"); }}
            />
          )}
        </SheetContent>
      </Sheet>

      <UpsellModal />
      {/* SpinWheel replaces the legacy ExitIntentModal — offers a coupon via a gamified UX instead */}
      <SpinWheel />
      <ReferralWidget />
      <LiveOrderToast />
    </div>
  );
}
